import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ThemeProvider, useTheme } from '../home/context/ThemeContext';
import { useSEO } from '../../utils/seo';

function DankeContent() {
  const { theme } = useTheme();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://seitenschmied.com';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useSEO({
    title: 'Danke für deine Anfrage | Seitenschmied',
    description: 'Vielen Dank für deine Anfrage. Wir melden uns schnellstmöglich bei dir.',
    canonical: `${siteUrl}/danke`,
    noindex: true,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/danke#webpage`,
      url: `${siteUrl}/danke`,
      name: 'Danke für deine Anfrage – Seitenschmied',
      description: 'Vielen Dank für deine Anfrage. Wir melden uns schnellstmöglich bei dir.',
      inLanguage: 'de-DE',
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    // form_submit: Google Ads importiert dieses Event automatisch aus GA4
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'form_submit',
      event_category: 'Conversion',
      event_label: 'Termin erfolgreich gebucht'
    });
  }, []);

  return (
    <div style={{ fontFamily: theme.fonts.body }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md" style={{ backgroundColor: 'rgba(255, 255, 255, 0.98)', boxShadow: '0 2px 20px rgba(0,0,0,0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img
                src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png"
                alt="seitenschmied"
                className="h-7 sm:h-8 w-auto"
              />
              <span className="hidden sm:block w-px h-4 bg-gray-300"></span>
              <span className="hidden sm:inline-flex items-center gap-1.5 text-gray-400 text-[10px] tracking-widest uppercase font-medium whitespace-nowrap">Websites made with <span className="text-amber-400">🧠</span></span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-10">
              <Link to="/#looks" className="font-medium text-base hover:opacity-70 transition-all duration-300 whitespace-nowrap" style={{ color: theme.colors.text, cursor: 'pointer' }}>Looks</Link>
              <Link to="/#pakete" className="font-medium text-base hover:opacity-70 transition-all duration-300 whitespace-nowrap" style={{ color: theme.colors.text, cursor: 'pointer' }}>Preise</Link>
              <Link to="/#prozess" className="font-medium text-base hover:opacity-70 transition-all duration-300 whitespace-nowrap" style={{ color: theme.colors.text, cursor: 'pointer' }}>Prozess</Link>
              <Link to="/#extras" className="font-medium text-base hover:opacity-70 transition-all duration-300 whitespace-nowrap" style={{ color: theme.colors.text, cursor: 'pointer' }}>Extras</Link>
              <Link to="/termin-buchen">
                <button
                  className="px-6 sm:px-8 py-3 font-semibold text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:scale-105 whitespace-nowrap"
                  style={{
                    background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
                    color: '#FFFFFF',
                    cursor: 'pointer',
                  }}
                >
                  Termin buchen
                </button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden w-11 h-11 flex items-center justify-center rounded-lg transition-colors" 
              style={{ color: theme.colors.text, cursor: 'pointer', backgroundColor: mobileMenuOpen ? theme.colors.accent + '20' : 'transparent' }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3 border-t pt-4" style={{ borderColor: theme.colors.accent + '20' }}>
              <Link 
                to="/#looks" 
                className="block py-3 px-4 rounded-lg font-medium text-base transition-all"
                style={{ color: theme.colors.text, cursor: 'pointer', backgroundColor: theme.colors.cardBg }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Looks
              </Link>
              <Link 
                to="/#pakete" 
                className="block py-3 px-4 rounded-lg font-medium text-base transition-all"
                style={{ color: theme.colors.text, cursor: 'pointer', backgroundColor: theme.colors.cardBg }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Preise
              </Link>
              <Link 
                to="/#prozess" 
                className="block py-3 px-4 rounded-lg font-medium text-base transition-all"
                style={{ color: theme.colors.text, cursor: 'pointer', backgroundColor: theme.colors.cardBg }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Prozess
              </Link>
              <Link 
                to="/#extras" 
                className="block py-3 px-4 rounded-lg font-medium text-base transition-all"
                style={{ color: theme.colors.text, cursor: 'pointer', backgroundColor: theme.colors.cardBg }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Extras
              </Link>
              <Link to="/termin-buchen" onClick={() => setMobileMenuOpen(false)}>
                <button
                  className="w-full py-4 font-bold text-base rounded-xl transition-all duration-300 whitespace-nowrap"
                  style={{
                    background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
                    color: '#FFFFFF',
                    cursor: 'pointer',
                  }}
                >
                  Termin buchen
                </button>
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section with Background Image */}
      <section
        className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 overflow-hidden min-h-screen flex items-center"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=aerial%20view%20of%20beautiful%20bavarian%20countryside%20w%C3%BCrmtal%20region%20green%20forests%20rolling%20hills%20small%20villages%20peaceful%20landscape%20sunny%20day%20natural%20beauty%20southern%20germany&width=1920&height=1080&seq=dankeBg1&orientation=landscape)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="max-w-3xl mx-auto text-center relative z-10">
          {/* Animated Checkmark */}
          <div className="mb-8 inline-flex items-center justify-center w-28 h-28 sm:w-32 sm:h-32 rounded-full animate-bounce" style={{ backgroundColor: theme.colors.accent + '30', backdropFilter: 'blur(10px)' }}>
            <i className="ri-checkbox-circle-line text-7xl sm:text-8xl" style={{ color: '#FFFFFF' }}></i>
          </div>
          
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white"
            style={{ fontFamily: theme.fonts.heading }}
          >
            Danke für deine Anfrage!
          </h1>
          
          <p
            className="text-xl sm:text-2xl mb-10 sm:mb-12 leading-relaxed text-white/95"
          >
            Wir melden uns schnellstens bei dir.
          </p>

          {/* Trust Elements */}
          <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4 sm:gap-6 mb-10 sm:mb-12">
            <div className="flex items-center gap-3 bg-white/15 backdrop-blur-md px-5 py-3 rounded-full">
              <i className="ri-time-line text-2xl" style={{ color: theme.colors.accent }}></i>
              <span className="font-semibold text-base text-white">Antwort &lt; 24h</span>
            </div>
            <div className="flex items-center gap-3 bg-white/15 backdrop-blur-md px-5 py-3 rounded-full">
              <i className="ri-shield-check-line text-2xl" style={{ color: theme.colors.accent }}></i>
              <span className="font-semibold text-base text-white">Dein Termin ist gesichert</span>
            </div>
            <div className="flex items-center gap-3 bg-white/15 backdrop-blur-md px-5 py-3 rounded-full">
              <i className="ri-map-pin-line text-2xl" style={{ color: theme.colors.accent }}></i>
              <span className="font-semibold text-base text-white">Lokal im Würmtal</span>
            </div>
          </div>

          <Link to="/">
            <button
              className="w-full sm:w-auto px-10 sm:px-12 py-4 sm:py-5 font-bold text-base sm:text-lg rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl whitespace-nowrap"
              style={{
                background: `linear-gradient(135deg, ${theme.colors.accent} 0%, ${theme.colors.accent}dd 100%)`,
                color: '#FFFFFF',
                cursor: 'pointer',
                boxShadow: `0 10px 30px ${theme.colors.accent}4d`,
              }}
            >
              Zurück zur Startseite
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default function Danke() {
  return (
    <ThemeProvider>
      <DankeContent />
    </ThemeProvider>
  );
}